import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useSettings } from "@/contexts/SettingsContext";
import { computeOrder, formatNum, GRAIN_TYPES } from "@/lib/growthLogic";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocalInput() {
  return toLocalInput(new Date().toISOString());
}

export function OrderFormDialog({ open, onOpenChange, order, onSaved }) {
  const { settings } = useSettings();
  const isEdit = Boolean(order);

  const [form, setForm] = useState(() => ({
    customer_name: "",
    street_address: "",
    town: "",
    municipality: "",
    county: "",
    zip_code: "",
    order_quantity_kg: 100,
    start_datetime: nowLocalInput(),
    grain_to_grass_grain_kg: settings.grain_to_grass_grain_kg,
    grain_to_grass_grass_kg: settings.grain_to_grass_grass_kg,
    target_weight_per_tray: settings.target_weight_per_tray,
    standard_cycle_days: settings.standard_cycle_days,
    grain_type: "barley",
    notes: "",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (order) {
      setForm({
        customer_name: order.customer_name,
        street_address: order.street_address || "",
        town: order.town || "",
        municipality: order.municipality || "",
        county: order.county || "",
        zip_code: order.zip_code || "",
        order_quantity_kg: order.order_quantity_kg,
        start_datetime: toLocalInput(order.start_datetime),
        grain_to_grass_grain_kg: order.grain_to_grass_grain_kg,
        grain_to_grass_grass_kg: order.grain_to_grass_grass_kg,
        target_weight_per_tray: order.target_weight_per_tray,
        standard_cycle_days: order.standard_cycle_days,
        grain_type: order.grain_type || "barley",
        notes: order.notes || "",
      });
    } else {
      setForm({
        customer_name: "",
        street_address: "",
        town: "",
        municipality: "",
        county: "",
        zip_code: "",
        order_quantity_kg: 100,
        start_datetime: nowLocalInput(),
        grain_to_grass_grain_kg: settings.grain_to_grass_grain_kg,
        grain_to_grass_grass_kg: settings.grain_to_grass_grass_kg,
        target_weight_per_tray: settings.target_weight_per_tray,
        standard_cycle_days: settings.standard_cycle_days,
        grain_type: "barley",
        notes: "",
      });
    }
  }, [open, order, settings]);

  const update = (key) => (e) => {
    const v = e.target.type === "number" ? parseFloat(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [key]: e.target.type === "number" && Number.isNaN(v) ? 0 : v }));
  };

  const preview = useMemo(() => {
    if (!form.order_quantity_kg || !form.target_weight_per_tray) return null;
    const previewOrder = {
      ...form,
      start_datetime: new Date(form.start_datetime || Date.now()).toISOString(),
    };
    return computeOrder(previewOrder);
  }, [form]);

  const handleSubmit = async () => {
    if (!form.customer_name?.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!(form.order_quantity_kg > 0)) {
      toast.error("Order quantity must be greater than zero");
      return;
    }
    if (!form.start_datetime) {
      toast.error("Start date & time is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        street_address: (form.street_address || "").trim(),
        town: (form.town || "").trim(),
        municipality: (form.municipality || "").trim(),
        county: (form.county || "").trim(),
        zip_code: (form.zip_code || "").trim(),
        order_quantity_kg: Number(form.order_quantity_kg),
        start_datetime: new Date(form.start_datetime).toISOString(),
        grain_to_grass_grain_kg: Number(form.grain_to_grass_grain_kg),
        grain_to_grass_grass_kg: Number(form.grain_to_grass_grass_kg),
        target_weight_per_tray: Number(form.target_weight_per_tray),
        standard_cycle_days: Number(form.standard_cycle_days),
        grain_type: form.grain_type || "barley",
        notes: form.notes || "",
      };
      if (isEdit) {
        await api.put(`/orders/${order.id}`, payload);
        toast.success("Order updated");
      } else {
        await api.post("/orders", payload);
        toast.success("Order created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="order-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? "Edit Order" : "Add New Order"}
          </DialogTitle>
          <DialogDescription>
            Customer order details. Ratios default to global settings — adjust per order if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs text-stone-600">Customer Name</Label>
              <Input
                data-testid="order-customer-name"
                value={form.customer_name}
                onChange={update("customer_name")}
                placeholder="e.g., Greenfield Dairy"
                className="mt-1.5"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
                Customer Address <span className="font-normal normal-case tracking-normal text-stone-400">(optional · shown only in Excel export)</span>
              </Label>
              <div className="mt-2 grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs text-stone-600">Street Address</Label>
                  <Input
                    data-testid="order-street-address"
                    value={form.street_address}
                    onChange={update("street_address")}
                    placeholder="e.g., Storgatan 12"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Town</Label>
                  <Input
                    data-testid="order-town"
                    value={form.town}
                    onChange={update("town")}
                    placeholder="e.g., Uppsala"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Municipality</Label>
                  <Input
                    data-testid="order-municipality"
                    value={form.municipality}
                    onChange={update("municipality")}
                    placeholder="e.g., Uppsala kommun"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">County / Län</Label>
                  <Input
                    data-testid="order-county"
                    value={form.county}
                    onChange={update("county")}
                    placeholder="e.g., Uppsala län"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Zip Code</Label>
                  <Input
                    data-testid="order-zip-code"
                    value={form.zip_code}
                    onChange={update("zip_code")}
                    placeholder="e.g., 753 20"
                    className="mt-1.5"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Order Quantity (kg of grass)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                data-testid="order-quantity"
                value={form.order_quantity_kg}
                onChange={update("order_quantity_kg")}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Start Date & Time</Label>
              <Input
                type="datetime-local"
                data-testid="order-start-datetime"
                value={form.start_datetime}
                onChange={update("start_datetime")}
                className="mt-1.5"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-stone-600">Grain Type</Label>
              <Select
                value={form.grain_type}
                onValueChange={(v) => setForm((f) => ({ ...f, grain_type: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="order-grain-type">
                  <SelectValue placeholder="Select a grain" />
                </SelectTrigger>
                <SelectContent>
                  {GRAIN_TYPES.map((g) => (
                    <SelectItem
                      key={g.value}
                      value={g.value}
                      data-testid={`grain-option-${g.value}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${g.dot}`} />
                        {g.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
              Order-specific Ratios
            </Label>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                <div>
                  <Label className="text-xs text-stone-600">Grain (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="order-grain-kg"
                    value={form.grain_to_grass_grain_kg}
                    onChange={update("grain_to_grass_grain_kg")}
                    className="mt-1.5"
                  />
                </div>
                <div className="pb-2.5 text-stone-400">→</div>
                <div>
                  <Label className="text-xs text-stone-600">Grass (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="order-grass-kg"
                    value={form.grain_to_grass_grass_kg}
                    onChange={update("grain_to_grass_grass_kg")}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-stone-600">Tray Yield (kg / tray)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="order-tray-weight"
                  value={form.target_weight_per_tray}
                  onChange={update("target_weight_per_tray")}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Cycle (days)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="1"
                  data-testid="order-cycle-days"
                  value={form.standard_cycle_days}
                  onChange={update("standard_cycle_days")}
                  className="mt-1.5"
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <Label className="text-xs text-stone-600">Notes (optional)</Label>
            <Textarea
              data-testid="order-notes"
              value={form.notes}
              onChange={update("notes")}
              className="mt-1.5"
              rows={2}
            />
          </div>

          {preview && (
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500 mb-3">
                Live Calculation
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-stone-500 text-xs">Trays needed</div>
                  <div className="font-mono text-lg text-stone-900" data-testid="preview-trays">
                    {formatNum(preview.traysNeededRaw, 2)}{" "}
                    <span className="text-stone-400 text-xs">({preview.traysNeededRounded} rnd)</span>
                  </div>
                </div>
                <div>
                  <div className="text-stone-500 text-xs">Grain required</div>
                  <div className="font-mono text-lg text-stone-900" data-testid="preview-grain">
                    {formatNum(preview.grainNeeded, 2)} kg
                  </div>
                </div>
                <div>
                  <div className="text-stone-500 text-xs">Cycle</div>
                  <div className="font-mono text-lg text-stone-900">
                    {form.standard_cycle_days}d
                  </div>
                </div>
                <div>
                  <div className="text-stone-500 text-xs">Yield/tray</div>
                  <div className="font-mono text-lg text-stone-900">
                    {form.target_weight_per_tray} kg
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="order-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="order-save-btn"
            className="bg-stone-900 hover:bg-stone-800 text-white"
          >
            {saving ? "Saving..." : isEdit ? "Update Order" : "Create Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
